/**
 * Honest progress. Steps and their labels come from the server
 * (pipeline/runner.py STEP_LABELS); nothing is invented here.
 *
 * С 2026-09-04 у прогона ОДИН голос на продуктовой поверхности — кнопка анализа
 * (UX_SPEC §2, §12): она и есть индикатор, и текст на ней собирает `T.run.busy(шаг, сек)`.
 * Поэтому отсюда ушла шапка (своё название состояния и свой счётчик секунд — второй и третий
 * экземпляры того же), а шаг и секунды отдаются наружу двумя функциями. Сам компонент —
 * полная таблица шагов, и живёт она в служебной панели: смотреть на неё надо, отлаживая
 * прогон, а не решая, ехать ли.
 */

import { useEffect, useState } from "react";

import { T } from "../strings";
import type { ProgressEvent } from "../api/types";
import type { RunState } from "../state/useWorkspace";

const statusClass = (status: ProgressEvent["status"]): string => `k-step--${status}`;

/** Latest event per step, in the order the server numbered them. */
const collapse = (events: ProgressEvent[]): ProgressEvent[] => {
  const latest = new Map<string, ProgressEvent>();
  for (const event of events) latest.set(event.step, event);
  return [...latest.values()].sort((a, b) => a.index - b.index);
};

/**
 * Что делается прямо сейчас, словами сервера. `null` — шага ещё нет: POST открыт, а первый
 * шаг сервер присылает не мгновенно, и кнопка тогда печатает одни секунды.
 */
export const currentStepLabel = (runState: RunState): string | null => {
  const steps = collapse(runState.events);
  return steps.length === 0 ? null : (steps[steps.length - 1]!.label ?? null);
};

/**
 * Секунды с начала прогона, тикающие. Хук, а не поле состояния: считает их таймер, а не
 * сервер, и владеть им должен тот, кто их печатает.
 */
export const useElapsedSeconds = (runState: RunState): number => {
  const [elapsed, setElapsed] = useState(0);
  const phase = runState.phase;
  const startedAt = runState.startedAt;

  useEffect(() => {
    if (phase !== "waiting" && phase !== "running") {
      setElapsed(0);
      return;
    }
    const started = startedAt ?? Date.now();
    setElapsed(Math.round((Date.now() - started) / 1000));
    const timer = setInterval(() => setElapsed(Math.round((Date.now() - started) / 1000)), 500);
    return () => clearInterval(timer);
  }, [phase, startedAt]);

  return elapsed;
};

interface Props {
  runState: RunState;
}

export function RunProgress({ runState }: Props) {
  if (runState.phase === "idle") return null;

  const steps = collapse(runState.events);

  return (
    <section className="k-section k-section--tight" aria-live="polite" data-testid="run-steps">
      {steps.length === 0 && runState.phase === "waiting" && (
        <p className="k-caption">{T.run.waitingServer}</p>
      )}

      <div className="k-steps">
        {steps.map((step) => (
          <div key={step.step} className={`k-step ${statusClass(step.status)}`}>
            <span className="k-step__index k-mono">
              {step.index}/{step.total}
            </span>
            <span className="k-step__label">{step.label}</span>
            <span className="k-step__detail">{step.detail ?? ""}</span>
          </div>
        ))}
      </div>

      {runState.error && (
        <div className="k-note k-note--error" style={{ marginTop: "var(--gap-2)" }}>
          {runState.error}
        </div>
      )}
    </section>
  );
}
